import { useCallback, useEffect, useMemo, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { shopModeApi } from "../../api/shopMode";
import { floorInventoryEndpoints } from "./floorInventoryEndpoints";
import type { InventoryBalance, ItemCategory, Location } from "../../api/types";
import { buildItemOptionsFromStock, LogWasteModal } from "../LogWasteModal";
import { ShopFloorBouquetThumb } from "./ShopFloorBouquetThumb";
import { ShopFloorIcon } from "./ShopFloorIcon";
import { useT } from "../../i18n/LanguageProvider";
import { colourLabel, flowerTypeLabel } from "../../i18n/taxonomy";
import { categoryLabel } from "../../i18n/taxonomy";
import { uomLabel } from "./shopFloorFormat";
import { compareText, numberText } from "../../i18n/intl";

interface Props {
  onExit(): void;
  showBack?: boolean;
}

export function ShopFloorWasteFlow({ onExit, showBack = true }: Props) {
  const { t } = useT();
  const [location, setLocation] = useState<Location | null>(null);
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedColorId, setSelectedColorId] = useState("");
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [rows, setRows] = useState<InventoryBalance[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<{
    itemId: string;
    name: string;
  } | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setInitialLoading(true);
    setError(null);
    void Promise.all([
      shopModeApi.floorLocation(),
      shopModeApi.floorListItemCategories(),
      shopModeApi.floorBalance({}),
    ])
      .then(([nextLocation, nextCategories, nextRows]) => {
        if (cancelled) return;
        // Sorted by the TRANSLATED name: ordering by the server's English
        // while showing Russian is a list that looks shuffled (S5).
        const sortedCategories = [...nextCategories].sort((a, b) =>
          compareText(
            categoryLabel(a.code, a.label),
            categoryLabel(b.code, b.label),
          ),
        );
        setLocation(nextLocation);
        setCategories(sortedCategories);
        setRows(nextRows);
        setSelectedCategoryId(
          sortedCategories.find((category) => category.code === "FLOWER")?.id ??
            sortedCategories[0]?.id ??
            "",
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          errorText(err, "waste.couldNotLoadOptions"),
        );
      })
      .finally(() => {
        if (!cancelled) setInitialLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshRows = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      setRows(await shopModeApi.floorBalance({}));
    } catch (err: unknown) {
      setError(
        errorText(err, "waste.couldNotRefresh"),
      );
    } finally {
      setRefreshing(false);
    }
  }, []);

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);
  const itemOptionsFromStock = useMemo(
    () => buildItemOptionsFromStock(rows),
    [rows],
  );
  const products = useMemo(
    () =>
      [...itemOptionsFromStock.entries()]
        .map(([itemId, entry]) => ({
          itemId,
          item: entry.item,
          qtyOnHand: entry.zones.reduce((sum, zone) => sum + zone.qty, 0),
        }))
        .sort((a, b) => compareText(a.item.name, b.item.name)),
    [itemOptionsFromStock],
  );

  const categoryProducts = useMemo(
    () =>
      products.filter(
        ({ item }) => !selectedCategoryId || item.category.id === selectedCategoryId,
      ),
    [products, selectedCategoryId],
  );

  const colors = useMemo(() => {
    const byId = new Map(
      categoryProducts.flatMap(({ item }) =>
        item.color ? [[item.color.id, item.color] as const] : [],
      ),
    );
    return [...byId.values()].sort((a, b) =>
      compareText(colourLabel(a.name), colourLabel(b.name)),
    );
  }, [categoryProducts]);

  const subCategories = useMemo(() => {
    const byId = new Map(
      categoryProducts.flatMap(({ item }) =>
        item.subCategoryRef
          ? [[item.subCategoryRef.id, item.subCategoryRef] as const]
          : [],
      ),
    );
    return [...byId.values()].sort((a, b) => compareText(a.label, b.label));
  }, [categoryProducts]);

  const visibleProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return categoryProducts.filter(({ item }) => {
      if (selectedColorId && item.color?.id !== selectedColorId) return false;
      if (
        selectedSubCategoryId &&
        item.subCategoryRef?.id !== selectedSubCategoryId
      ) {
        return false;
      }
      if (query && !item.name.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [
    categoryProducts,
    searchQuery,
    selectedColorId,
    selectedSubCategoryId,
  ]);

  const chooseCategory = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setSelectedColorId("");
    setSelectedSubCategoryId("");
    setSelectedProduct(null);
    setMessage(null);
  };

  return (
    <div className="shop-floor-stock-flow shop-floor-waste-flow">
      <div className="shop-floor-waste-toolbar">
        <div className="shop-floor-stock-flow-header">
          {showBack ? (
            <button
              type="button"
              className="button plain shop-floor-back-btn"
              onClick={onExit}
            >
              {t("common.back")}
            </button>
          ) : null}
          <p className="shop-floor-stock-flow-title">{t("hub.recordWaste")}</p>
          <p className="muted shop-floor-stock-flow-intro">{t("waste.findItem")}</p>
        </div>

        <label className="sf-bouquet-search shop-floor-waste-search">
          <ShopFloorIcon name="search" size={21} />
          <input
            type="search"
            placeholder={t("waste.searchPlaceholder")}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            aria-label={t("waste.searchLabel")}
            autoComplete="off"
          />
        </label>

        {showBack ? (
          <button
            type="button"
            className="button plain shop-floor-waste-refresh"
            onClick={() => void refreshRows()}
            disabled={refreshing}
          >
            {refreshing ? t("waste.refreshing") : t("waste.refresh")}
          </button>
        ) : null}
      </div>

      {message ? <div className="alert success">{message}</div> : null}
      {error ? <div className="alert error">{error}</div> : null}

      {initialLoading ? (
        <div className="card empty-state">{t("waste.loadingProducts")}</div>
      ) : (
        <>
          <div className="sf-bouquet-filter-panel shop-floor-waste-filters">
            <div className="sf-filter-row">
              <span className="sf-filter-label">{t("waste.filterCat")}</span>
              <div
                className="sf-filter-chips"
                role="tablist"
                aria-label={t("waste.filterByCategory")}
              >
                {categories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    role="tab"
                    aria-selected={selectedCategoryId === category.id}
                    className={`sf-tab-chip${
                      selectedCategoryId === category.id
                        ? " sf-tab-chip--active"
                        : ""
                    }`}
                    onClick={() => chooseCategory(category.id)}
                  >
                    {categoryLabel(category.code, category.label)}
                  </button>
                ))}
              </div>
            </div>

            {colors.length > 0 ? (
              <div className="sf-filter-row">
                <span className="sf-filter-label">{t("waste.filterColor")}</span>
                <div
                  className="sf-filter-chips"
                  role="group"
                  aria-label={t("waste.filterByColor")}
                >
                  <button
                    type="button"
                    aria-pressed={selectedColorId === ""}
                    className={`sf-color-chip${
                      selectedColorId === "" ? " sf-color-chip--active" : ""
                    }`}
                    onClick={() => setSelectedColorId("")}
                  >
                    {t("common.all")}
                  </button>
                  {colors.map((color) => (
                    <button
                      key={color.id}
                      type="button"
                      aria-pressed={selectedColorId === color.id}
                      className={`sf-color-chip${
                        selectedColorId === color.id
                          ? " sf-color-chip--active"
                          : ""
                      }`}
                      onClick={() => setSelectedColorId(color.id)}
                    >
                      <span
                        className="sf-color-dot"
                        style={{ background: color.hex ?? "#bbb" }}
                        aria-hidden="true"
                      />
                      {colourLabel(color.name)}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {selectedCategory?.code === "FLOWER" && subCategories.length > 0 ? (
              <div className="sf-filter-row">
                <span className="sf-filter-label">{t("waste.filterType")}</span>
                <div
                  className="sf-filter-chips"
                  role="group"
                  aria-label={t("waste.filterByType")}
                >
                  <button
                    type="button"
                    aria-pressed={selectedSubCategoryId === ""}
                    className={`sf-type-chip${
                      selectedSubCategoryId === "" ? " sf-type-chip--active" : ""
                    }`}
                    onClick={() => setSelectedSubCategoryId("")}
                  >
                    {t("common.all")}
                  </button>
                  {subCategories.map((subCategory) => (
                    <button
                      key={subCategory.id}
                      type="button"
                      aria-pressed={selectedSubCategoryId === subCategory.id}
                      className={`sf-type-chip${
                        selectedSubCategoryId === subCategory.id
                          ? " sf-type-chip--active"
                          : ""
                      }`}
                      onClick={() => setSelectedSubCategoryId(subCategory.id)}
                    >
                      {flowerTypeLabel(subCategory.code, subCategory.label)}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          {visibleProducts.length === 0 ? (
            <div className="card empty-state shop-floor-waste-empty">
              {categoryProducts.length === 0
                ? selectedCategory
                  ? t("waste.noStockInCategory", {
                      category: selectedCategory.label.toLowerCase(),
                    })
                  : t("waste.noStockMatching")
                : t("waste.noFilterMatch")}
            </div>
          ) : (
            <div
              className="shop-floor-waste-grid shop-floor-waste-results"
              aria-label={t("waste.productsWithStock")}
            >
              {visibleProducts.map(({ itemId, item, qtyOnHand }) => (
                <button
                  key={itemId}
                  type="button"
                  className="shop-floor-waste-product-btn"
                  onClick={() =>
                    setSelectedProduct({ itemId, name: item.name })
                  }
                >
                  <span className="shop-floor-waste-product-thumb">
                    <ShopFloorBouquetThumb
                      id={itemId}
                      imageKey={item.imageKey}
                      alt={item.name}
                    />
                  </span>
                  <span className="shop-floor-waste-product-copy">
                    <strong>{item.name}</strong>
                    <span className="muted">
                      {[
                        item.color ? colourLabel(item.color.name) : null,
                        item.subCategoryRef
                          ? flowerTypeLabel(
                              item.subCategoryRef.code,
                              item.subCategoryRef.label,
                            )
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                      {item.color || item.subCategoryRef ? " · " : ""}
                      {t("waste.qtyInStock", {
                        qty: numberText(qtyOnHand),
                        unit: uomLabel(item.defaultUom, qtyOnHand),
                      })}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {selectedProduct && location ? (
        <LogWasteModal
          locationId={location.id}
          itemOptionsFromStock={itemOptionsFromStock}
          initialItemId={selectedProduct.itemId}
          initialItemDisplayName={selectedProduct.name}
          inventoryEndpoints={floorInventoryEndpoints}
          onClose={() => setSelectedProduct(null)}
          onSuccess={() => {
            setMessage(t("waste.recordedFor", { name: selectedProduct.name }));
            setSelectedProduct(null);
            void refreshRows();
          }}
        />
      ) : null}
    </div>
  );
}
